import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getRequests, getMyBookmarks } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { MapPin, Search, X, Bookmark, Clock, ArrowUp, Users, ArrowUpDown, Truck, SlidersHorizontal, ChevronDown, Navigation, Heart, LayoutGrid } from 'lucide-react';
import { ListingCard, CardRow, CardTitle, CardPrice } from '../components/ListingCard';
import { cn, parseLocalDate, getRateTypeLabel } from '../lib/utils';
import DirectoryLayout, { useBreakpoint } from '../components/layouts/DirectoryLayout';
import RequestDetailPage from './RequestDetailPage';
import { readVenueName } from '../constants/profileContract';

// Urgency countdown helper
function getUrgencyInfo(eventDate, urgency) {
  if (!eventDate) return null;
  const now = new Date();
  const event = parseLocalDate(eventDate);
  const hoursLeft = Math.max(0, Math.round((event - now) / 3600000));
  if (urgency === 'CRITICAL' || hoursLeft < 24) return { label: `${hoursLeft}h`, variant: 'urgent' };
  if (hoursLeft < 72) return { label: `${Math.round(hoursLeft / 24)}d`, variant: 'soon' };
  return null;
}

// Budget tiers
const BUDGET_OPTIONS = [
  { label: '<€250', max: 250 },
  { label: '€250–500', min: 250, max: 500 },
  { label: '€500+', min: 500 },
];

// Role chips
const MUSICIAN_ROLE_CHIPS = ['Vocals', 'Guitar', 'Drums', 'Keys', 'DJ', 'Bass', 'Sax'];
const TECH_ROLE_CHIPS = ['Sound', 'Lights', 'Stage', 'AV', 'Backline', 'Monitor', 'FOH'];

// Genre chips for requests
import { GENRES } from '../constants/taxonomy';

const SORT_OPTIONS = [
  { key: null, label: 'Best match' },
  { key: 'pay_high', label: 'Highest pay' },
  { key: 'pay_low', label: 'Lowest pay' },
  { key: 'date_soon', label: 'Soonest' },
  { key: 'newest', label: 'Newest' },
];

// Travel radius options
const TRAVEL_RADIUS_OPTIONS = [
  { key: '0-25', label: '0–25km', min: 0, max: 25 },
  { key: '25-50', label: '25–50km', min: 25, max: 50 },
  { key: '50+', label: '50km+', min: 50 },
];

// Date helpers
function startOfToday() { const d = new Date(); d.setHours(0,0,0,0); return d; }
function daysFromNow(n) { const d = startOfToday(); d.setDate(d.getDate() + n); return d; }
function getNextFriday() { const d = startOfToday(); d.setDate(d.getDate() + ((5 - d.getDay() + 7) % 7 || 7)); return d; }
function getNextSunday() { const f = getNextFriday(); const d = new Date(f); d.setDate(d.getDate() + 2); d.setHours(23,59,59,999); return d; }

const DATE_OPTIONS = [
  { key: 'week', label: 'This week', from: () => startOfToday(), to: () => daysFromNow(7) },
  { key: 'weekend', label: 'Weekend', from: getNextFriday, to: getNextSunday },
  { key: '30d', label: '30 days', from: () => startOfToday(), to: () => daysFromNow(30) },
];

function formatEventDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const day = d.getDate();
  const month = d.toLocaleDateString('en', { month: 'short' }).toUpperCase();
  const weekday = d.toLocaleDateString('en', { weekday: 'short' });
  return { day, month, weekday };
}

export default function RequestsPage() {
  const { user } = useAuth();
  const params = useParams();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('MUSICIAN');
  const selectedId = params.id || null;
  const [dateFilter, setDateFilter] = useState(null);
  const [budgetFilter, setBudgetFilter] = useState(null);
  const [savedOnly, setSavedOnly] = useState(false);
  const [roleSubtypeFilter, setRoleSubtypeFilter] = useState(null);
  const [travelRadiusFilter, setTravelRadiusFilter] = useState(null);
  const [genreFilter, setGenreFilter] = useState(null);
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [sortBy, setSortBy] = useState('date_soon');
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const sortRef = useRef(null);

  /* The scroll-to-top button only renders on the phone/tablet list. Anywhere else (the gig page
     below 1024px is this component too) the listener would re-render the whole page each time the
     scroll crosses 400px, for a button that never shows. */
  const listShowing = isMobile && !selectedId;
  useEffect(() => {
    if (!listShowing) { setShowScrollTop(false); return undefined; }
    const onScroll = () => setShowScrollTop(window.scrollY > 400);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [listShowing]);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const buildQueryParams = () => {
    const params = {};
    if (dateFilter) {
      const from = dateFilter.from();
      const to = dateFilter.to();
      params.startDate = from.toISOString().split('T')[0];
      params.endDate = to.toISOString().split('T')[0];
    }
    if (budgetFilter) {
      if (budgetFilter.min !== undefined) params.minBudget = budgetFilter.min;
      if (budgetFilter.max !== undefined) params.maxBudget = budgetFilter.max;
    }
    if (user?.profileLat && user?.profileLng) {
      params.user_lat = user.profileLat;
      params.user_lng = user.profileLng;
    }
    return params;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadRequests(); }, [dateFilter, budgetFilter, sortBy, user?.profileLat]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (user) loadBookmarks(); }, [user]);

  const loadRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = buildQueryParams();
      if (sortBy) params.sort = sortBy;
      const response = await getRequests(params);
      setRequests(response.data);
    } catch (err) {
      setError('Failed to load requests. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadBookmarks = async () => {
    try {
      const response = await getMyBookmarks();
      setBookmarkedIds(new Set(response.data));
    } catch (err) { console.error('Failed to load bookmarks:', err); }
  };

  const filteredRequests = useMemo(() => requests.filter((r) => {
    if (savedOnly && !bookmarkedIds.has(r.id)) return false;
    if (roleFilter && r.roleTypeNeeded !== roleFilter) return false;
    if (roleSubtypeFilter) {
      const q = roleSubtypeFilter.toLowerCase();
      const hit = [r.roleSubtype, r.title].some(v => v?.toLowerCase().includes(q));
      if (!hit) return false;
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const hit = [r.title, r.city, readVenueName(r), r.roleSubtype]
        .some(v => v?.toLowerCase().includes(q));
      if (!hit) return false;
    }
    if (genreFilter) {
      const q = genreFilter.toLowerCase();
      const hit = [r.title, r.description, r.roleSubtype]
        .some(v => v?.toLowerCase().includes(q));
      if (!hit) return false;
    }
    if (travelRadiusFilter && r.travelDistanceKm != null) {
      const km = r.travelDistanceKm;
      if (travelRadiusFilter.min !== undefined && km < travelRadiusFilter.min) return false;
      if (travelRadiusFilter.max !== undefined && km > travelRadiusFilter.max) return false;
    }
    return true;
  }), [requests, roleFilter, searchQuery, savedOnly, bookmarkedIds, roleSubtypeFilter, genreFilter, travelRadiusFilter]);

  const hasFilters = searchQuery || dateFilter || budgetFilter || savedOnly || roleSubtypeFilter || travelRadiusFilter || genreFilter;

  const activeFilterCount = [dateFilter, budgetFilter, roleSubtypeFilter, travelRadiusFilter, genreFilter].filter(Boolean).length;

  // Close sort dropdown on outside click
  useEffect(() => {
    if (!sortDropdownOpen) return;
    const handler = (e) => { if (sortRef.current && !sortRef.current.contains(e.target)) setSortDropdownOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [sortDropdownOpen]);

  const clearAll = () => {
    setSearchQuery('');
    setDateFilter(null); setBudgetFilter(null); setSavedOnly(false); setRoleSubtypeFilter(null); setTravelRadiusFilter(null); setGenreFilter(null);
  };

  const currentRoleChips = roleFilter === 'MUSICIAN' ? MUSICIAN_ROLE_CHIPS : TECH_ROLE_CHIPS;

  if (loading) {
    return (
      <div className="page-container-lg requests-page">
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-28 bg-[var(--s1)] animate-pulse" style={{ borderRadius: 'var(--s8-radius-lg)' }}></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container-lg requests-page">
        <div className="text-center py-12">
          <p className="text-[var(--red)] text-sm mb-3" data-testid="error-message">{error}</p>
          <button onClick={loadRequests} className="text-[var(--s8-amber)] hover:text-[var(--t0)] text-sm" data-testid="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  /* ── Sort dropdown (reused across layouts) ─── */
  const sortDropdown = (
    <div className="relative" ref={sortRef}>
      <button onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
        className="flex items-center gap-1.5 px-3.5 h-9 text-xs font-semibold uppercase tracking-wider transition-all"
        style={{ fontFamily: 'var(--s8-font-condensed)', color: 'var(--s8-text-muted)', border: '1px solid var(--s8-border-card)', borderRadius: 'var(--s8-radius-md)' }}
        data-testid="sort-dropdown-btn"
      >
        {SORT_OPTIONS.find(o => o.key === sortBy)?.label || 'Best match'}
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {sortDropdownOpen && (
        <div className="absolute left-0 top-full mt-1 z-50 py-1 min-w-[160px]" style={{ background: 'var(--s8-bg-surface)', border: '1px solid var(--s8-border-card)', boxShadow: '0 8px 24px rgba(0,0,0,0.4)', borderRadius: 'var(--s8-radius-md)' }}>
          {SORT_OPTIONS.map(opt => (
            <button key={opt.key || 'default'} onClick={() => { setSortBy(opt.key); setSortDropdownOpen(false); }}
              className="w-full text-left px-4 py-2 text-sm transition-colors"
              style={{ fontFamily: 'var(--s8-font-body)', color: sortBy === opt.key ? 'var(--s8-amber)' : 'var(--s8-text-body)', background: sortBy === opt.key ? 'rgba(200,146,62,0.06)' : 'transparent' }}
              data-testid={`sort-${opt.key || 'default'}`}
            >{opt.label}</button>
          ))}
        </div>
      )}
    </div>
  );

  /* ── Filter panel content (shared between desktop sidebar and tablet drawer) ─── */
  const filterPanel = (
    <>
      {/* SHOWING */}
      <div className="sidebar-section">
        <span className="browse-filter-label block mb-2">Showing</span>
        <div className="flex items-center gap-1.5 flex-wrap">
          {[
            { key: 'MUSICIAN', label: 'For Musicians' },
            { key: 'TECH', label: 'For Tech Crew' },
          ].map((opt) => (
            <button
              key={opt.key}
              onClick={() => { setRoleFilter(opt.key); setRoleSubtypeFilter(null); }}
              className={cn('browse-chip', roleFilter === opt.key && 'browse-chip-active')}
              data-testid={`role-tab-${opt.key.toLowerCase()}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-3 mb-3 browse-input w-full">
        <Search className="h-4 w-4 shrink-0 text-[var(--s8-text-muted)]" />
        <input type="text" placeholder="Search title, venue, city..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
          className="bg-transparent border-none outline-none w-full text-[var(--s8-text-body)] placeholder:text-[var(--s8-text-muted)]"
          style={{ fontFamily: 'var(--s8-font-body)', fontSize: '14px', padding: 0 }} data-testid="search-input" />
      </div>

      {/* WHEN */}
      <div className="sidebar-section">
        <span className="browse-filter-label block mb-2">When</span>
        <div className="flex items-center gap-1.5 flex-wrap">
          {DATE_OPTIONS.map((opt) => (
            <button key={opt.key} onClick={() => setDateFilter(dateFilter?.key === opt.key ? null : opt)} className={cn('browse-chip', dateFilter?.key === opt.key && 'browse-chip-active')} data-testid={`filter-date-${opt.key}`}>{opt.label}</button>
          ))}
        </div>
      </div>

      {/* PAY */}
      <div className="sidebar-section">
        <span className="browse-filter-label block mb-2">Pay</span>
        <div className="flex items-center gap-1.5 flex-wrap">
          {BUDGET_OPTIONS.map((opt) => (
            <button key={opt.label} onClick={() => setBudgetFilter(budgetFilter?.label === opt.label ? null : opt)} className={cn('browse-chip', budgetFilter?.label === opt.label && 'browse-chip-active')} data-testid={`filter-budget-${opt.label}`}>{opt.label}</button>
          ))}
        </div>
      </div>

      {/* TRAVEL */}
      <div className="sidebar-section">
        <span className="browse-filter-label block mb-2">Travel</span>
        <div className="flex items-center gap-1.5 flex-wrap">
          {TRAVEL_RADIUS_OPTIONS.map((opt) => (
            <button key={opt.key} onClick={() => setTravelRadiusFilter(travelRadiusFilter?.key === opt.key ? null : opt)} className={cn('browse-chip', travelRadiusFilter?.key === opt.key && 'browse-chip-active')} data-testid={`filter-radius-${opt.key}`}>{opt.label}</button>
          ))}
        </div>
      </div>

      {/* ROLE */}
      <div className="sidebar-section">
        <span className="browse-filter-label block mb-2">Role</span>
        <div className="flex items-center gap-1.5 flex-wrap">
          {currentRoleChips.map((role) => (
            <button key={role} onClick={() => setRoleSubtypeFilter(roleSubtypeFilter === role ? null : role)} className={cn('browse-chip', roleSubtypeFilter === role && 'browse-chip-active')} data-testid={`filter-role-subtype-${role.toLowerCase()}`}>{role}</button>
          ))}
        </div>
      </div>

      {/* GENRE */}
      <div className="sidebar-section">
        <span className="browse-filter-label block mb-2">Genre</span>
        <div className="flex items-center gap-1.5 flex-wrap">
          {GENRES.map((genre) => (
            <button key={genre} onClick={() => setGenreFilter(genreFilter === genre ? null : genre)} className={cn('browse-chip', genreFilter === genre && 'browse-chip-active')} data-testid={`filter-genre-${genre.toLowerCase()}`}>{genre}</button>
          ))}
        </div>
      </div>

      {/* Saved + Clear */}
      <div className="sidebar-section">
        <div className="flex items-center gap-2">
          <button onClick={() => setSavedOnly(!savedOnly)} className={cn('browse-chip flex items-center gap-1.5', savedOnly && 'browse-chip-active')} data-testid="saved-filter">
            <Bookmark className={cn('h-3 w-3', savedOnly && 'fill-[var(--s8-accent)]')} />Saved
          </button>
          {hasFilters && (
            <button onClick={clearAll} className="browse-chip flex items-center gap-1.5 hover:text-[var(--red)] hover:border-[var(--red)]" style={{ color: 'var(--s8-text-muted)', borderColor: 'var(--s8-border-card)' }} data-testid="filter-clear-all">
              <X className="h-3 w-3" />Clear all
            </button>
          )}
        </div>
      </div>
    </>
  );

  /* ── Card list (reused) ─── */
  const handleCardClick = (requestId, e) => {
    if (isMobile) return; // Let <Link> handle navigation on mobile
    e.preventDefault();
    navigate(`/requests/${requestId}`);
  };

  const cardList = filteredRequests.length === 0 ? (
    <div className="text-center py-12">
      <p className="text-[var(--s8-text-muted)] text-sm">No requests match your filters</p>
      {hasFilters && <button onClick={clearAll} className="mt-3 text-[var(--s8-accent)] hover:text-[var(--s8-text-main)] text-sm">Clear All Filters</button>}
    </div>
  ) : (
    filteredRequests.map((request, index) => {
      const { day, month } = formatEventDate(request.eventDate);
      const isBookmarked = bookmarkedIds.has(request.id);
      const urgencyInfo = getUrgencyInfo(request.eventDate, request.urgency);
      const requestHref = request.reference || request.id;
      const isSelected = !isMobile && (selectedId === request.id || (request.reference && selectedId === request.reference));
      const cardClass = cn(
        urgencyInfo?.variant === 'urgent' ? 's8-card--urgent' : urgencyInfo?.variant === 'soon' ? 's8-card--soon' : '',
        isSelected && 's8-card--selected'
      );
      const title = request.title?.replace('[DEMO] ', '');
      const venue = [readVenueName(request), request.city].filter(Boolean).join(' · ');
      return (
        <ListingCard key={request.id} to={`/requests/${requestHref}`} index={index} cardClass={cardClass} current={!!isSelected} testId={`request-card-${request.id}`}
          onClick={(e) => handleCardClick(requestHref, e)}
          stub={<><span className="s8-stub-value">{day}</span><span className="s8-stub-label">{month}</span></>}
        >
          <CardRow><CardTitle>{title}</CardTitle><div className="flex items-center gap-2 shrink-0"><CardPrice amount={request.budgetAmount ? `€${request.budgetAmount}` : null} sub={request.budgetAmount ? getRateTypeLabel(request.rateType) : null} /></div></CardRow>
          <CardRow>
            <div className="s8-venue" style={{ margin: 0 }}><MapPin className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{venue || 'Location TBD'}</span></div>
            <div className="flex items-center gap-2 shrink-0">{isBookmarked && <Bookmark className="h-3.5 w-3.5 fill-[var(--s8-accent)] text-[var(--s8-accent)]" />}</div>
          </CardRow>
          <CardRow>
            <div className="flex items-center gap-3 flex-wrap">
              {request.startTime && request.endTime && <span className="s8-time"><Clock className="h-3 w-3" />{request.startTime} – {request.endTime}</span>}
              {request.timeline?.callTime && <span className="s8-time" style={{ color: 'var(--teal, #4A9E8E)' }}><Truck className="h-3 w-3" />Call {request.timeline.callTime}</span>}
              {request.travelMinutes != null && (
                <span className="s8-time" style={{ color: 'var(--s8-amber)' }} data-testid={`travel-badge-${request.id}`}>
                  <Navigation className="h-3 w-3" />~{request.travelMinutes} min
                </span>
              )}
            </div>
          </CardRow>
        </ListingCard>
      );
    })
  );

  /* ── Empty state for detail pane ─── */
  const emptyState = (
    <div className="flex flex-col items-center justify-center h-full" style={{ color: 'var(--t3)' }}>
      <LayoutGrid className="h-12 w-12 mb-4" style={{ color: 'var(--s8-amber)', opacity: 0.3 }} />
      <h2 style={{ fontFamily: 'var(--s8-font-display)', fontWeight: 600, fontSynthesis: 'none', fontSize: '22px', letterSpacing: '-0.01em', lineHeight: 1.15, color: 'var(--t1)' }}>Select a Gig to See Details</h2>
      <p className="mt-2" style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '12px' }}>{filteredRequests.length} open requests across the Netherlands</p>
    </div>
  );

  /* ── Detail pane content ─── */
  const detailPane = selectedId ? (
    <div className="px-5 py-2">
      <RequestDetailPage requestId={selectedId} embedded key={selectedId} />
    </div>
  ) : null;

  /* ═══════ MOBILE: If a specific request is selected, show detail page ═══════ */
  if (isMobile && selectedId) {
    return <RequestDetailPage />;
  }

  /* ═══════ DESKTOP / TABLET: Three-pane layout ═══════ */
  if (!isMobile) {
    return (
      <div className="requests-page">
        <DirectoryLayout
          title="Open Requests"
          count={filteredRequests.length}
          filters={filterPanel}
          list={cardList}
          detail={detailPane}
          emptyState={emptyState}
          sortControl={sortDropdown}
          mobileContent={null}
        />
      </div>
    );
  }

  /* ═══════ MOBILE: Existing layout unchanged ═══════ */
  return (
    <div className="browse-page requests-page px-4 sm:px-6 py-6 sm:py-8 pb-10">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-baseline justify-between">
          <h1 className="browse-title">Open Requests</h1>
          <span className="text-xs text-[var(--s8-text-muted)]" style={{ fontFamily: 'var(--s8-font-mono)' }} data-testid="available-count">{filteredRequests.length} available</span>
        </div>
      </div>

      <div className="browse-layout">
        <aside className="browse-sidebar hidden lg:block">{filterPanel}</aside>

        <div className="browse-main">
          {/* Mobile filter pill + sort */}
          <div className="flex items-center gap-3 mb-4 lg:hidden" style={{ height: '44px' }}>
            <button onClick={() => setFilterSheetOpen(true)}
              className="flex items-center gap-2 px-3.5 h-9 text-xs font-semibold uppercase tracking-wider transition-all"
              style={{ fontFamily: 'var(--s8-font-condensed)', color: activeFilterCount > 0 ? 'var(--s8-amber)' : 'var(--s8-text-muted)', border: `1px solid ${activeFilterCount > 0 ? 'var(--s8-amber)' : 'var(--s8-border-card)'}`, background: activeFilterCount > 0 ? 'rgba(200,146,62,0.08)' : 'transparent', borderRadius: 'var(--s8-radius-md)' }}
              data-testid="filters-pill"
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />Filters{activeFilterCount > 0 ? ` · ${activeFilterCount}` : ''}
            </button>
            {sortDropdown}
          </div>

          {/* Mobile Filter Bottom Sheet */}
          {filterSheetOpen && (
            <div className="fixed inset-0 z-50 lg:hidden" data-testid="filter-sheet">
              <div className="absolute inset-0 bg-black/50" onClick={() => setFilterSheetOpen(false)} />
              <div className="absolute bottom-0 left-0 right-0 px-5 pt-5 pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] animate-slide-up" style={{ background: 'var(--s8-bg-surface)', border: '1px solid var(--s8-border-card)', borderBottom: 'none', maxHeight: '75vh', overflowY: 'auto', borderTopLeftRadius: 'var(--s8-radius-xl)', borderTopRightRadius: 'var(--s8-radius-xl)' }}>
                <div className="flex items-center justify-between mb-5">
                  <button onClick={clearAll} className="text-xs transition-colors" style={{ fontFamily: 'var(--s8-font-body)', color: 'var(--s8-text-muted)' }} data-testid="filter-clear-all-mobile">Clear All</button>
                  <span className="text-sm font-semibold" style={{ fontFamily: 'var(--s8-font-condensed)', color: 'var(--s8-text-main)', letterSpacing: '1px', textTransform: 'uppercase' }}>Filters</span>
                  <button onClick={() => setFilterSheetOpen(false)} className="p-1" style={{ color: 'var(--s8-text-muted)' }}><X className="h-4 w-4" /></button>
                </div>
                {filterPanel}
                <button onClick={() => setFilterSheetOpen(false)} className="w-full py-3 text-sm font-semibold uppercase tracking-wider transition-all mt-4" style={{ fontFamily: 'var(--s8-font-condensed)', background: 'var(--s8-amber)', color: 'var(--bg)', borderRadius: 'var(--s8-radius-md)' }} data-testid="filter-apply-btn">Apply</button>
              </div>
            </div>
          )}

          {/* Cards */}
          <div className="browse-card-grid-wrapper">{cardList}</div>
        </div>
      </div>

      {showScrollTop && (
        <button onClick={scrollToTop} className="fixed bottom-[calc(1.5rem+env(safe-area-inset-bottom,0px))] right-[calc(1.5rem+env(safe-area-inset-right,0px))] z-50 h-10 w-10 rounded-full bg-[var(--s8-accent)] text-black flex items-center justify-center shadow-lg hover:brightness-110 transition-all" data-testid="scroll-to-top-btn">
          <ArrowUp className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
